import React, { useCallback, useEffect, useMemo } from 'react';

import classnames from 'classnames';

import { useMediaQuery } from '@mui/material';

import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import Image, { TRANSFORMATIONS } from 'components/uiLibrary/Image';
import ImageAvatar from 'components/uiLibrary/ImageAvatar';
import KebabMenu from 'components/uiLibrary/KebabMenu';
import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import TabsNavigation from 'components/uiLibrary/TabsNavigation';
import Typography from 'components/uiLibrary/Typography';

import AppliedFilters from 'components/Filters/Display/AppliedFilters';
// eslint-disable-next-line import/no-cycle
import FilterFacets, { FILTER_LAYOUT } from 'components/Filters/Display/FilterFacets';
import useTracking from 'components/Globals/Analytics';
import { COMPONENTS, GOOGLE_OLD_TRACKING_SERVICES, SECTIONS } from 'components/Globals/Analytics/constants';
import { useShowPageAccessPortalForCasting } from 'components/Globals/PortalsProvider';
import ShareEntity from 'components/Globals/ShareEntity';
import CalendarHeaders from 'components/Productions/Display/CalendarProductionListing/Calendar/CalendarHeaders';
import CalendarRangeNavigation from 'components/Productions/Display/CalendarProductionListing/Calendar/CalendarRangeNavigation';

import { DATE_FORMATS, ENTITY_MAIN_TABS, SEASON_TYPES, TP } from 'constants/index';

import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import { useDialogs } from 'utils/hooks/useDialogs';
import useEntitySectionTabs from 'utils/hooks/useEntitySectionTabs';
import usePageContext from 'utils/hooks/usePageContext';
import useSelectedViewMode from 'utils/hooks/useSelectedViewMode';
import useSharedCalendarRange from 'utils/hooks/useSharedCalendarRange';
import { ARTIST_PRO_VIEW, TRACK_EVENTS } from 'utils/tracking';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES, FILTER_TYPES, VIEW_MODES } from 'operabase-router/constants';

import classes from './ProViewStickyHeader.module.scss';

const trackingData = {
  section: SECTIONS.PRO_VIEW_STICKY_HEADER,
};

const useActionItems = ({ onClickHandler }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { navigate, entity, entityType } = usePageContext();

  return useMemo(() => {
    const items = [];

    items.push(
      <ShareEntity
        key="share"
        className={classes.shareEntity}
        trackingData={{ section: SECTIONS.HEADER, component: COMPONENTS.SHARE_CTA }}
        asKebabMenu
      />,
    );

    items.push(
      <div className={classes.publicViewKebabMenuItem}>
        <Typography size="14" weight="medium">
          {t(`${TP}.FN_PUBLIC_SITE`)}
        </Typography>
        <LinkButton
          variant="text"
          size="medium"
          styles={{
            root: classes.publicViewCTA,
          }}
          onClick={onClickHandler}
          rightIcon={<SpriteIcon icon="arrow_right_link" size={12} />}
          disableHover
          disableUnderline
          preventDefault
          stopPropagation
        >
          <Typography size="12" italic>
            {t(`${TP}.VIEW_IN`)}
          </Typography>
          <Typography weight="medium" size="12">
            {t(`${TP}.FN_PUBLIC_SITE`)}
          </Typography>
        </LinkButton>
      </div>,
    );

    return items;
  }, [entity, entityType, navigate, t, onClickHandler]);
};

const getCustomFilterGroups = (tab, viewMode) => {
  const isDesiredRole = tab === ENTITY_MAIN_TABS.DESIRED_ROLE;
  const isRepertoireTab = tab === ENTITY_MAIN_TABS.REPERTOIRE;
  const isCalendarTab = viewMode === VIEW_MODES.CALENDAR;
  const filterGroups = isCalendarTab
    ? [
        { name: `${TP}.FN_GENRE`, filterTypes: [FILTER_TYPES.CREATOR] },
        { name: `${TP}.m_PROD`, filterTypes: [FILTER_TYPES.WORK] },
        { name: `${TP}.FN_PRODUCER`, filterTypes: [FILTER_TYPES.PRODUCER] },
        { name: `${TP}.WHO`, filterTypes: [FILTER_TYPES.WHO] },
        { name: `${TP}.WHERE`, filterTypes: [FILTER_TYPES.WHERE] },
        {
          name: `${TP}.FN_ROLE_TITLE`,
          filterTypes: [FILTER_TYPES.ROLE],
        },
      ]
    : [
        { name: `${TP}.m_COMPOSER`, filterTypes: [FILTER_TYPES.COMPOSER], isDisabled: isDesiredRole },
        { name: `${TP}.FN_MUSICAL_WORKS`, filterTypes: [FILTER_TYPES.WORK], isDisabled: isDesiredRole },
        {
          name: `${TP}.ROLE_PROFESSIONS_FILTER`,
          filterTypes: [FILTER_TYPES.ROLE_PROFESSION_REPERTOIRE],
          isDisabled: isDesiredRole,
        },
        ...(isDesiredRole ? [{ name: `${TP}.FN_TYPE`, filterTypes: [FILTER_TYPES.TYPE], isDisabled: true }] : []),
        ...(isRepertoireTab ? [{ name: `${TP}.FN_GENRE_STAGING`, filterTypes: [FILTER_TYPES.GENRE_STAGING] }] : []),
      ];

  return {
    [FILTER_LAYOUT.BUTTON_GROUP_DETAILED]: filterGroups,
  };
};

const customFilterGroupStyles = tab =>
  tab === ENTITY_MAIN_TABS.CALENDAR
    ? { root: classes.filterContainerDetailed, filterGroupBox: classes.filterGroupBox }
    : { root: classes.filterFacets, filterGroupBox: classes.filterFacetItem };

const Filters = ({ commonFilterParams, layout, mainPath, scrollable = true, tab, viewMode }) => (
  <FilterFacets
    commonFilterParams={commonFilterParams}
    layout={layout || FILTER_LAYOUT.ICON}
    scrollable={scrollable}
    mainPath={mainPath}
    {...(([ENTITY_MAIN_TABS.REPERTOIRE, ENTITY_MAIN_TABS.DESIRED_ROLE].includes(tab) ||
      (tab === ENTITY_MAIN_TABS.CALENDAR && viewMode === VIEW_MODES.CALENDAR)) && {
      customGroup:
        tab === ENTITY_MAIN_TABS.CALENDAR ? getCustomFilterGroups(tab, viewMode) : getCustomFilterGroups(tab),
      styles: customFilterGroupStyles(tab),
    })}
    trackingData={{
      ...trackingData,
      component: COMPONENTS.FILTERS,
    }}
  />
);

const Profile = ({ entity }) => (
  <div
    className={classes.profile}
    onClick={() => {
      if (!window) {
        return;
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }}
  >
    {entity?.image?.medium ? (
      <Image
        alt={entity.name}
        src={entity?.image?.medium}
        transformations={TRANSFORMATIONS.PROFILE_THUMBNAIL}
        disableNextImage
        lazy={false}
        width={34}
        height={34}
        className={classes.profile__image}
      />
    ) : (
      <ImageAvatar
        name={entity?.name}
        height={34}
        width={34}
        fontSize={16}
        shape="circle"
        type="V4"
        className={classes.profile__imageAvatar}
      />
    )}
    <Typography truncate>
      <Typography size="12" weight="bold" truncate>
        {entity?.name}
      </Typography>
      <Typography size="10">{entity?.headline}</Typography>
    </Typography>
    <span className={classes.profile__searchSection}>
      <SpriteIcon icon="search" size="14" />
    </span>
  </div>
);

const ProViewStickyHeader = ({ isScrolledBeyond }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const track = useTracking();
  const isSmallScreen = useMediaQuery('(max-width: 800px)');
  const { entity, baseRoute, mainPath, navigate, entityType, hideGlobalHeader, showGlobalHeader, permissions } =
    usePageContext();
  const { viewType, rangeStartDate, rangeEndDate, onShiftLeft, onShiftRight, allowLeftShift, allowRightShift } =
    useSharedCalendarRange();
  const { obRouteContext, isLoggedIn } = useAppContext();
  const { setIsLoginDialog } = useDialogs();
  const { filterParams } = obRouteContext || {};
  const { filters } = obRouteContext?.linkProps || {};
  const { castingToolPermissions, isAdmin } = permissions;
  const hasCastingToolAccess = isAdmin || castingToolPermissions?.hasAccess;
  const { viewMode } = useSelectedViewMode({ filters });
  const showPageAccessPortalForCasting = useShowPageAccessPortalForCasting();

  const hasAppliedFilters = useMemo(() => {
    const appliedFilters = Object.keys(filterParams || {})?.filter(
      key =>
        ![
          FILTER_TYPES.BOOLEAN_SEARCH,
          FILTER_TYPES.SINCE_YEAR,
          FILTER_TYPES.BOOLEAN_SEARCH,
          FILTER_TYPES.SEARCH_EXPRESSION,
          FILTER_TYPES.CTX_ENTITY,
        ].includes(key),
    );
    return Object.keys(appliedFilters).length > 0;
  }, [filterParams]);
  const tabs = useEntitySectionTabs({ entityType: ENTITY_TYPES.PROFILE, entity, baseRoute, filters });
  const activeTab = tabs?.find(tab => tab.key === mainPath)?.key || tabs?.[0]?.key;
  const commonFilterParams = {
    include_virtual_contributors: true,
    ...(activeTab?.key === ENTITY_MAIN_TABS.CALENDAR && {
      date_from: createDate().format(DATE_FORMATS.FULL_DATE),
      aggregation_season_type: SEASON_TYPES.CALENDAR_YEAR,
    }),
  };
  const showFilters = useMemo(
    () =>
      [
        ENTITY_MAIN_TABS.PERFORMANCES,
        ENTITY_MAIN_TABS.REPERTOIRE,
        ENTITY_MAIN_TABS.DESIRED_ROLE,
        ENTITY_MAIN_TABS.CALENDAR,
      ].includes(activeTab),
    [activeTab],
  );

  useEffect(() => {
    if (isScrolledBeyond) {
      hideGlobalHeader();
    } else {
      showGlobalHeader();
    }
  }, [isScrolledBeyond, hideGlobalHeader, showGlobalHeader]);

  const onClickHandler = useCallback(() => {
    track.click(
      {
        name: TRACK_EVENTS.CLICK,
        data: {
          id: ARTIST_PRO_VIEW.ARTIST_PRO_VIEW_SWITCH,
          tab: 'public',
        },
      },
      GOOGLE_OLD_TRACKING_SERVICES,
    );
  }, [track]);

  const actionItems = useActionItems({
    onClickHandler,
  });

  if (!isScrolledBeyond) {
    return null;
  }

  if (isSmallScreen) {
    return (
      <div className={classes.stickyHeader}>
        <div className={classes.stickyHeader__container}>
          <div className={classes.stickyHeader__top}>
            <Profile entity={entity} />
            <KebabMenu options={actionItems} />
          </div>
          <HorizontalScroller hideArrow>
            <div className={classes.stickyHeader__bottom}>
              <TabsNavigation
                tabs={tabs}
                activeTab={activeTab}
                trackingData={{
                  ...trackingData,
                  component: COMPONENTS.TABS,
                }}
                ns="NS_ENTITY_STUB_PAGE"
                handleOnClick={(tab, e) => {
                  if (!isLoggedIn) {
                    e.preventDefault();
                    setIsLoginDialog({ isOpen: true });
                    return;
                  }

                  if (!hasCastingToolAccess) {
                    e.preventDefault();
                    showPageAccessPortalForCasting();
                    return;
                  }

                  navigate.to(tab?.linkProps);
                  navigate.scrollTo({ behavior: 'auto' });
                }}
                variant="dark"
                asDropdown
              />
              {hasCastingToolAccess && (
                <Filters
                  layout={FILTER_LAYOUT.HORIZONTAL}
                  commonFilterParams={commonFilterParams}
                  trackingData={trackingData}
                  viewMode={viewMode}
                />
              )}
            </div>
          </HorizontalScroller>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.stickyHeader}>
      <div
        className={classnames(classes.stickyHeader__container, {
          [classes.stickyHeader__containerTab]: [
            ENTITY_MAIN_TABS.ROLE_GRAPH,
            ENTITY_MAIN_TABS.VENUE_GRAPH,
            ENTITY_MAIN_TABS.ORG_GRAPH,
          ].includes(activeTab),
          [classes.stickyHeader__containerCalendar]:
            ENTITY_MAIN_TABS.CALENDAR === activeTab && viewMode === VIEW_MODES.CALENDAR,
        })}
      >
        <div className={classes.stickyHeader__castingTop}>
          <Profile entity={entity} />
          <ShareEntity
            showLabelText={false}
            className={classes.shareEntity}
            trackingData={{ ...trackingData, component: COMPONENTS.SHARE_CTA }}
            sticky
          />
          <div className={classes.castingDivider} />
          {!hasAppliedFilters && hasCastingToolAccess && (
            <>
              <Filters layout={FILTER_LAYOUT.BUTTON} commonFilterParams={commonFilterParams} />
              <div className={classes.castingDivider} />
            </>
          )}
          <HorizontalScroller hideArrow>
            <div className={classes.stickyHeader__tabs}>
              <TabsNavigation
                tabs={tabs}
                activeTab={activeTab}
                trackingData={{
                  ...trackingData,
                  component: COMPONENTS.TABS,
                }}
                ns="NS_ENTITY_STUB_PAGE"
                handleOnClick={(tab, e) => {
                  navigate.scrollTo({ behavior: 'auto' });

                  if (!isLoggedIn) {
                    e.preventDefault();
                    setIsLoginDialog({ isOpen: true });
                    return;
                  }

                  if (!hasCastingToolAccess) {
                    e.preventDefault();
                    showPageAccessPortalForCasting();
                  }
                }}
                variant="dark"
              />
            </div>
          </HorizontalScroller>
          <LinkButton
            variant="orange"
            size="medium"
            isV4
            rightIcon={<SpriteIcon icon="keyboard_tab" size={16} className={classes.publicViewIcon} />}
            trackingData={{
              ...trackingData,
              component: COMPONENTS.VIEW_IN_PUBLIC_SITE_CTA,
            }}
            onClick={onClickHandler}
            {...navigate.getLinkProps({
              entity,
              entityType,
              onlyLinkProps: true,
            })}
            isLink
          >
            <Typography size="12" italic>
              {t(`${TP}.VIEW_IN`)}
            </Typography>
            &nbsp;
            <Typography color="orange" size="12" weight="bold">
              {t(`${TP}.PUBLIC_SITE`)}
            </Typography>
          </LinkButton>
        </div>
        {hasAppliedFilters && hasCastingToolAccess && (
          <div className={classes.stickyHeader__middle}>
            <Filters
              layout={FILTER_LAYOUT.BUTTON}
              commonFilterParams={commonFilterParams}
              trackingData={trackingData}
            />
            <div className={classes.castingDivider} />
            <AppliedFilters trackingData={trackingData} />
          </div>
        )}
        {showFilters && hasCastingToolAccess && (
          <div className={classes.stickyHeader__filters}>
            <div>
              <Filters
                layout={FILTER_LAYOUT.BUTTON_GROUP_DETAILED}
                commonFilterParams={commonFilterParams}
                mainPath={mainPath}
                tab={activeTab}
                viewMode={viewMode}
              />
            </div>
            {ENTITY_MAIN_TABS.CALENDAR === activeTab && viewMode === VIEW_MODES.CALENDAR && (
              <div className={classes.stickyHeader__calendar_range}>
                <CalendarRangeNavigation
                  viewType={viewType}
                  startDate={rangeStartDate}
                  endDate={rangeEndDate}
                  onShiftLeft={onShiftLeft}
                  onShiftRight={onShiftRight}
                  allowLeftShift={allowLeftShift}
                  allowRightShift={allowRightShift}
                />
                <CalendarHeaders viewType={viewType} rangeStartDate={rangeStartDate} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProViewStickyHeader;
